import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import type { Brand } from "effect/Brand"
import type * as Schema from "effect/Schema"
import type { Simplify } from "effect/Types"
import { RALColorValue } from "../RALColor.ts"
import { type HasTimestamps, TimestampProps } from "../Timestamps.ts"
import { OperatorNote, PriceId, SKU } from "./shared.ts"

export const OptionId = S.StringId
export type OptionId = S.StringId

function hasSpecialCharacters(value: string) {
  return !/^[a-zA-Z\u00c4\u00e4\u00d6\u00f6\u00dc\u00fc\u00df0-9!@#%*+=._-]+$/g.test(value)
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface OptionCodeBrand extends Simplify<Brand<"OptionCode"> & S.NonEmptyString255Brand> {
}

export type OptionCode = string & OptionCodeBrand

function isOptionCode(s: S.NonEmptyString255): s is OptionCode {
  return !hasSpecialCharacters(s)
}

export const OptionCode = (
  S.NonEmptyString255.pipe(
    S.refine(isOptionCode, {
      arbitraryConstraint: { patterns: [{ source: "^[A-Za-z0-9]{3,12}$", flags: "" }] }
    }),
    S.fromBrand(S.nominal<OptionCode>())
  ) as Schema.Codec<OptionCode, string>
)
  .pipe(S.withDefaultMake)

export const OptionVisibility = S.Literals(["visible", "hidden", "functional"])
export type OptionVisibility = typeof OptionVisibility.Type
export const OptionBase = {
  id: OptionId.withConstructorDefault,
  externalId: S.NullOr(S.NonEmptyString255),
  code: OptionCode,
  name: S.NonEmptyString255,
  //  optionGroupId // nest inside optionGroup instead. it nullable(is), but also required to be in an option group afaik
  // group - otherwise create one named "Konfiguration"
  description: S.NullOr(S.NonEmptyString2k).withConstructorDefault,
  tooltip: S.NullOr(S.NonEmptyString255).withConstructorDefault,
  note: OperatorNote,

  // TODO: priceTable; seems to never have duplicates, so could be embedded in the Option directly?
  // TODO: relationship... must remove on priceTable delete..
  priceTableId: S.NullOr(S.StringId),

  priceFormula: S.NullOr(S.NonEmptyString255).withConstructorDefault,

  isEnabled: S.Boolean.withConstructorDefault,
  isRequired: S.Boolean.pipe(S.defaultBool),
  visibility: OptionVisibility.pipe(S.withConstructorDefault(Effect.succeed("visible" as const))),
  isReadonly: S.Boolean.withConstructorDefault,
  // sortOrder; handle through order in the Group
  activateAt: S.NullOr(S.inputDate).withConstructorDefault,
  focussedImageUrl: S.NullOr(S.NonEmptyString2k).withConstructorDefault,

  ...TimestampProps
} satisfies S.Struct.Fields

export const ListOptionView = S.Literals(["pills", "list", "cards", "dekor", "cardinal"])
export type ListOptionView = typeof ListOptionView.Type

export const CARDINAL_ITEM_COUNT = 4

export const cardinalItemsFilter = S.makeFilter((input: unknown) => {
  const { items, view } = input as { view: ListOptionView; items: readonly unknown[] }
  return view === "cardinal" && items.length !== CARDINAL_ITEM_COUNT
    ? `Die Darstellungsart "cardinal" erfordert genau ${CARDINAL_ITEM_COUNT} Elemente (Nord, Ost, Süd, West)`
    : undefined
})

/**
 * Number options whose values give the proportions of the shape drawn in the
 * middle of a `cardinal` list: width spans the top/bottom sides, height the
 * left/right ones. Unset or without values, the shape keeps its default square.
 */
export class CardinalShape extends S.Opaque<CardinalShape, CardinalShape.Encoded>()(
  S
    .Struct({
      widthOptionId: S.NullOr(S.StringId).withConstructorDefault,
      heightOptionId: S.NullOr(S.StringId).withConstructorDefault
    })
    .annotate({ identifier: "CardinalShape" })
) {}

export class LinkedOption extends S.Opaque<LinkedOption, LinkedOption.Encoded>()(
  S
    .Struct({
      optionId: S.NullOr(S.StringId),
      alwaysShow: S.NullOr(S.Boolean).withConstructorDefault,
      override: S
        .NullOr(S.Struct({
          min: S.NullOr(S.NonNegativeNumber),
          max: S.NullOr(S.NonNegativeNumber),
          readOnly: S.NullOr(S.Boolean).withConstructorDefault,
          isInteger: S.optional(S.NullOr(S.Boolean)) // TODO: optional stinks
        }))
        .withConstructorDefault
    })
    .annotate({ identifier: "LinkedOption" })
) {}

export class ListOptionItem extends S.Opaque<ListOptionItem, ListOptionItem.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      value: S.NonEmptyString255,
      price: S.NonNegativeDecimal.withConstructorDefault,
      swissPrice: S.NullOr(S.NonNegativeDecimal).withConstructorDefault,
      priceId: PriceId,
      linkedOption: S.NullOr(LinkedOption).withConstructorDefault,
      skuFragment: S.NullOr(SKU).withConstructorDefault,
      isEnabled: S.Boolean.withConstructorDefault,
      isVisible: S.Boolean.pipe(S.defaultBool),
      /** when `true` this will make the item selected by default */
      isDefault: S.Boolean.withConstructorDefault,
      // TODO: Is this even necessary? Doesn't seem to be used
      isMutuallyExclusive: S.Boolean.withConstructorDefault,
      title: S.NonEmptyString255,
      subTitle: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      description: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      backgroundImage: S.NullOr(S.NonEmptyString2k).withConstructorDefault,
      backgroundColor: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      tooltip: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      activateAt: S.NullOr(S.inputDate).withConstructorDefault,
      categoryId: S.NullOr(S.StringId), // TODO
      priceTableId: S.NullOr(S.StringId),
      priceFormula: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      ...TimestampProps
    })
    .annotate({ identifier: "ListOptionItem" })
) implements HasTimestamps {}

export class ListOptionCategory extends S.Opaque<ListOptionCategory, ListOptionCategory.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      title: S.NonEmptyString255,
      tooltip: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      ...TimestampProps
    })
    .annotate({ identifier: "ListOptionCategory" })
) implements HasTimestamps {}

export class ListOption extends S.Opaque<ListOption, ListOption.Encoded>()(
  S
    .Struct({
      ...OptionBase,
      _tag: S.Literal("list"),
      defaultValue: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      view: ListOptionView.pipe(S.withConstructorDefault(Effect.succeed("pills" as const))),
      enableInput: S.Boolean.withConstructorDefault,
      inputPlaceholder: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      multiSelect: S.Boolean.withConstructorDefault,
      cardinalShape: S.NullOr(CardinalShape).withConstructorDefault,

      // what we could do is have uncategorisedItems, and categories (with items). there may not be duplicates however.
      items: S.NonEmptyArray(ListOptionItem),
      categories: S.Array(ListOptionCategory).withConstructorDefault
    })
    .pipe(S.check(cardinalItemsFilter))
    .annotate({ identifier: "ListOption" })
) implements HasTimestamps {}

export class SKUItem extends S.Opaque<SKUItem, SKUItem.Encoded>()(
  S
    .Struct({
      minValue: S.NonNegativeNumber,
      sku: SKU
    })
    .annotate({ identifier: "SKUItem" })
) {}

export class NumberOption extends S.Opaque<NumberOption, NumberOption.Encoded>()(
  S
    .Struct({
      ...OptionBase,
      _tag: S.Literal("number"),
      defaultValue: S.NullOr(S.NonNegativeNumber).withConstructorDefault,
      minValue: S.NullOr(S.NonNegativeNumber).withConstructorDefault,
      maxValue: S.NullOr(S.NonNegativeNumber).withConstructorDefault,
      maxDecimals: S.NullOr(S.NonNegativeInt).withConstructorDefault,
      stepSize: S.NullOr(S.Finite).withConstructorDefault,
      stepPrice: S.NullOr(S.Finite).withConstructorDefault,
      stepPriceDescription: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      priceId: PriceId,
      prefix: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      suffix: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      inputPlaceholder: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      skuList: S.Array(SKUItem).withConstructorDefault
    })
    .annotate({ identifier: "NumberOption" })
) implements HasTimestamps {}

export class TextOption extends S.Opaque<TextOption, TextOption.Encoded>()(
  S
    .Struct({
      ...OptionBase,
      _tag: S.Literal("text"),
      defaultValue: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      minLength: S.NullOr(S.NonNegativeInt).withConstructorDefault, // should be constrained to the possible value
      maxLength: S.NullOr(S.NonNegativeInt).withConstructorDefault, // should be constrained to the possible value
      inputPlaceholder: S.NullOr(S.NonEmptyString255).withConstructorDefault
    })
    .annotate({ identifier: "TextOption" })
) implements HasTimestamps {}

export class RALColorOption extends S.Opaque<RALColorOption, RALColorOption.Encoded>()(
  S
    .Struct({
      ...OptionBase,
      _tag: S.Literal("ral_color"),
      defaultValue: S.NullOr(RALColorValue).withConstructorDefault,
      inputPlaceholder: S.NullOr(S.NonEmptyString255).withConstructorDefault
    })
    .annotate({ identifier: "RALColorOption" })
) implements HasTimestamps {}

export class TextInfoOption extends S.Opaque<TextInfoOption, TextInfoOption.Encoded>()(
  S
    .Struct({
      ...OptionBase,
      _tag: S.Literal("text_info"),
      title: S.NullOr(S.NonEmptyString255).withConstructorDefault,
      includeInPdf: S.Boolean.withConstructorDefault,

      // not actually relevant for text info, like other various values inside OptionBase...
      defaultValue: S.NullOr(S.NonEmptyString255).withConstructorDefault
    })
    .annotate({ identifier: "TextInfoOption" })
) implements HasTimestamps {}

export const ConfiguratorOption = S.Union([ListOption, NumberOption, TextOption, RALColorOption, TextInfoOption])
export type ConfiguratorOption = typeof ConfiguratorOption.Type

export namespace ConfiguratorOption {
  export type Encoded = Schema.Codec.Encoded<typeof ConfiguratorOption>
}

// codegen:start {preset: model}
//
export namespace CardinalShape {
  export interface Encoded extends S.StructNestedEncoded<typeof CardinalShape> {}
}
export namespace LinkedOption {
  export interface Encoded extends S.StructNestedEncoded<typeof LinkedOption> {}
}
export namespace ListOptionItem {
  export interface Encoded extends S.StructNestedEncoded<typeof ListOptionItem> {}
}
export namespace ListOptionCategory {
  export interface Encoded extends S.StructNestedEncoded<typeof ListOptionCategory> {}
}
export namespace ListOption {
  export interface Encoded extends S.StructNestedEncoded<typeof ListOption> {}
}
export namespace SKUItem {
  export interface Encoded extends S.StructNestedEncoded<typeof SKUItem> {}
}
export namespace NumberOption {
  export interface Encoded extends S.StructNestedEncoded<typeof NumberOption> {}
}
export namespace TextOption {
  export interface Encoded extends S.StructNestedEncoded<typeof TextOption> {}
}
export namespace RALColorOption {
  export interface Encoded extends S.StructNestedEncoded<typeof RALColorOption> {}
}
export namespace TextInfoOption {
  export interface Encoded extends S.StructNestedEncoded<typeof TextInfoOption> {}
}
//
// codegen:end
